import * as React from 'react';
import { useState, useCallback } from 'react';
import styled from 'styled-components';

import HighlightingRulesContext from 'views/components/contexts/HighlightingRulesContext';
import { DocumentTitle, Spinner } from 'components/common';
import PageContentLayout from 'components/layout/PageContentLayout';
import useSearchConfiguration from 'hooks/useSearchConfiguration';
import useActiveSearch from 'data-lake/preview/hooks/useActiveSearch';
import DataLakePreviewStoreProvider from 'data-lake/preview/store/DataLakePreviewStoreProvider';
import useUrlParams from 'data-lake/preview/hooks/useUrlParams';
import useCurrentUser from 'hooks/useCurrentUser';
import { isPermitted } from 'util/PermissionsMixin';
import Panel from 'components/bootstrap/Panel';
import { Link } from 'components/common/router';
import DataLakeRoutes from 'data-lake/Routes';
import useLicenseValidityForSubject from 'license/hooks/useLicenseValidityForSubject';
import { LICENSE_SUBJECTS } from 'license/constants';
import { InvalidLicenseWarning } from 'license/LicenseCheck';
import type HighlightingRule from 'views/logic/views/formatting/highlighting/HighlightingRule';
import Sidebar from 'views/components/sidebar/Sidebar';
import DataLakeHighlightingRules from 'data-lake/preview/DataLakeHighlightingRules';
import useActiveDataLakes from 'data-lake/preview/hooks/useActiveDataLakes';
import type { ActivePreviewSearch, DataLakeURLQueryParams, DataLakeInfoArray } from 'data-lake/preview/Types';
import PreviewConfiguredDataLakesProvider from 'data-lake/preview/contexts/PreviewConfiguredDataLakesProvider';
import useSelectedDataLake from 'data-lake/hooks/useSelectedDataLake';
import type Search from 'views/logic/search/Search';
import usePluginEntities from 'hooks/usePluginEntities';
import FieldTypesProvider from 'data-lake/preview/contexts/FieldTypesProvider';
import findDataLakeIdInSearchType from 'data-lake/preview/utils/findDataLakeIdInSearchType';
import useFeature from 'hooks/useFeature';
import { DocumentationLink } from 'components/support';
import DocsHelper from 'util/DocsHelper';
import { DATA_LAKE_INTERNAL_ENABLED } from 'data-lake/Constants';

const initialSearchForDataLake = (selectedDataLakeId: string, activeSearch: Search) => {
  if (!selectedDataLakeId || !activeSearch) return undefined;

  const activeSearchDataLakeId = findDataLakeIdInSearchType(activeSearch);
  if (activeSearchDataLakeId === selectedDataLakeId && activeSearch) return activeSearch;

  return undefined;
};

type PreviewPageContentProps = {
  activeSearch: Search | undefined;
  searchJob: { status: ActivePreviewSearch['status']; id: string; nodeId: string; progress: number } | undefined;
  initialUrlParams: DataLakeURLQueryParams;
  resetUrlQueryParams: () => void;
};

const PreviewPageContent = ({
  activeSearch,
  initialUrlParams,
  searchJob,
  resetUrlQueryParams,
}: PreviewPageContentProps) => {
  const { selectedDataLake } = useSelectedDataLake();
  const dataLakePlugins = usePluginEntities('dataLakeStorageTypes');
  const dataLakePlugin = dataLakePlugins.find(({ storageType }) => storageType === selectedDataLake?.storageType);
  const PageComponent = dataLakePlugin?.previewComponent;
  const initialSearch = initialSearchForDataLake(selectedDataLake?.id, activeSearch);

  if (!PageComponent) {
    throw new Error(`No page component found for data lake storage type: ${selectedDataLake?.storageType}`);
  }

  return (
    <PageComponent
      key={selectedDataLake?.id} // This ensures we properly reset the state when switching data lakes. It simplifies the state managememnt in the child component a lot.
      initialSearch={initialSearch}
      initialUrlParams={initialUrlParams}
      initialSearchJob={searchJob}
      resetUrlQueryParams={resetUrlQueryParams}
    />
  );
};

const StyledPageContentLayout = styled(PageContentLayout)`
  .page-content-grid {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    overflow: auto;
  }
`;

const GridContainer = styled.div`
  display: flex;
  height: 100%;
`;

const DataLakePreviewPageComponent = ({ activeDataLakes }: { activeDataLakes: DataLakeInfoArray }) => {
  const { initialUrlParams, resetUrlQueryParams, setDataLakeIdUrlParam } = useUrlParams();
  const { config } = useSearchConfiguration();
  const [highlightingRules, setHighlightingRules] = useState<Array<HighlightingRule>>([]);
  const highlightSidebarSection = useCallback(
    () => <DataLakeHighlightingRules setHighlightingRules={setHighlightingRules} />,
    [],
  );
  const {
    search: activeSearch,
    result: activeSearchResult,
    searchJob,
    isLoading: isLoadingActiveSearch,
  } = useActiveSearch(initialUrlParams);

  return (
    <DocumentTitle title="Data Lake Preview">
      {config && !isLoadingActiveSearch ? (
        <PreviewConfiguredDataLakesProvider
          setDataLakeIdUrlParam={setDataLakeIdUrlParam}
          initialUrlParams={initialUrlParams}
          activeSearch={activeSearch}
          searchJobStatus={searchJob.status}
          activeDataLakes={activeDataLakes}>
          <DataLakePreviewStoreProvider result={activeSearchResult}>
            <FieldTypesProvider>
              <HighlightingRulesContext.Provider value={highlightingRules}>
                <GridContainer>
                  <Sidebar
                    sections={[
                      {
                        key: 'highlighting',
                        icon: 'format_paragraph' as const,
                        title: 'Highlighting',
                        content: highlightSidebarSection,
                      },
                    ]}
                    enableSidebarPinning={false}
                    actions={[]}
                    title="Data Lake Preview"
                  />
                  <StyledPageContentLayout>
                    <PreviewPageContent
                      activeSearch={activeSearch}
                      initialUrlParams={initialUrlParams}
                      searchJob={searchJob}
                      resetUrlQueryParams={resetUrlQueryParams}
                    />
                  </StyledPageContentLayout>
                </GridContainer>
              </HighlightingRulesContext.Provider>
            </FieldTypesProvider>
          </DataLakePreviewStoreProvider>
        </PreviewConfiguredDataLakesProvider>
      ) : (
        <Spinner />
      )}
    </DocumentTitle>
  );
};

const LicenseCheck = ({ children }: React.PropsWithChildren) => {
  const {
    data: { valid: isValidLicense },
    isInitialLoading: isLoadingLicense,
  } = useLicenseValidityForSubject(LICENSE_SUBJECTS.enterprise);

  if (isLoadingLicense) {
    return <Spinner />;
  }

  if (!isValidLicense) {
    return (
      <PageContentLayout>
        <InvalidLicenseWarning
          title="Data Lake Preview is disabled"
          featureName="Data Lake Preview"
          licenseSubject={LICENSE_SUBJECTS.enterprise}
          displayWarningContainer
          bsStyle="danger"
        />
      </PageContentLayout>
    );
  }

  return children;
};

type Props = {
  children: (props: { activeDataLakes: DataLakeInfoArray }) => React.ReactNode;
};

const SetupInstructions = ({
  hasPermissions,
  externalEnabled,
  internalEnabled,
}: {
  hasPermissions: boolean;
  externalEnabled: boolean;
  internalEnabled: boolean;
}) => {
  if (!hasPermissions) {
    return <>Contact your Administrator to set it up.</>;
  }
  if (externalEnabled && internalEnabled) {
    return (
      <>
        You can do that <Link to={DataLakeRoutes.BACKEND}>here</Link> for an internal Data Lake or{' '}
        <Link to={DataLakeRoutes.CONNECTORS}>here</Link> for a connector with an external Data Lake.
      </>
    );
  }
  if (internalEnabled) {
    return (
      <>
        You can do that <Link to={DataLakeRoutes.BACKEND}>here</Link>.
      </>
    );
  }
  if (externalEnabled) {
    return (
      <>
        You can do that <Link to={DataLakeRoutes.CONNECTORS}>here</Link>.
      </>
    );
  }

  return null;
};

const ActiveBackendCheck = ({ children }: Props) => {
  const currentUser = useCurrentUser();
  const {
    data: activeDataLakes,
    isInitialLoading: isInitialLoadingActiveDataLakes,
    hasActiveDataLake,
  } = useActiveDataLakes();
  const hasConfigPermissions = isPermitted(currentUser.permissions, 'data_lake_config:update');
  const externalDLFeatureIsEnabled = useFeature('external_data_lake_search');
  const internalDLFeatureIsEnabled = DATA_LAKE_INTERNAL_ENABLED;

  if (isInitialLoadingActiveDataLakes) {
    return <Spinner />;
  }

  if (!hasActiveDataLake) {
    return (
      <PageContentLayout>
        <Panel bsStyle="info">
          <Panel.Heading>There is no active Data Lake backend or connector</Panel.Heading>
          <Panel.Body>
            <>
              To access Data Lake Preview, a Data Lake must be configured in Graylog.{' '}
              <SetupInstructions
                hasPermissions={hasConfigPermissions}
                externalEnabled={externalDLFeatureIsEnabled}
                internalEnabled={internalDLFeatureIsEnabled}
              />{' '}
              For more details, see the <DocumentationLink page={DocsHelper.PAGES.DATA_LAKE} text="documentation" />.
            </>
          </Panel.Body>
        </Panel>
      </PageContentLayout>
    );
  }

  return children({ activeDataLakes });
};

const DataLakePreviewPage = () => (
  <LicenseCheck>
    <ActiveBackendCheck>
      {({ activeDataLakes }) => <DataLakePreviewPageComponent activeDataLakes={activeDataLakes} />}
    </ActiveBackendCheck>
  </LicenseCheck>
);

export default DataLakePreviewPage;
